import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { outcomesApi } from '@/lib/api/outcomes.api';

import { CALL_OUTCOME_BADGES, getOutcomeColorClass } from '@repo/shared-types';

import type { OutcomeDefinition, OutcomeDefinitionInput } from '@repo/shared-types';

/** All outcome definitions — management view (includes inactive rows). */
export function useOutcomeDefinitions(params?: { activeOnly?: boolean }) {
  return useQuery({
    queryKey: ['outcomes', 'list', params?.activeOnly ?? false],
    queryFn: () => outcomesApi.list(params),
    staleTime: 30_000,
  });
}

/**
 * Active platform-wide outcome vocabulary — what filters, badges and pickers
 * should offer.
 */
export function useEffectiveOutcomes() {
  return useQuery({
    queryKey: ['outcomes', 'effective'],
    queryFn: () => outcomesApi.effective(),
    staleTime: 30_000,
  });
}

export function useCreateOutcome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OutcomeDefinitionInput) => outcomesApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['outcomes'] }),
  });
}

export function useUpdateOutcome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<OutcomeDefinitionInput> }) =>
      outcomesApi.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['outcomes'] }),
  });
}

export function useDeactivateOutcome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => outcomesApi.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['outcomes'] }),
  });
}

/**
 * Badge class + label lookup for outcome keys, built from the dynamic
 * definitions with a static fallback for keys that predate them (or while
 * the query is still loading).
 */
export function buildOutcomeDisplay(definitions: OutcomeDefinition[] | undefined) {
  const byKey = new Map((definitions ?? []).map((d) => [d.key, d]));
  return {
    badgeClass: (key: string | null | undefined): string => {
      if (!key) return getOutcomeColorClass(null);
      const def = byKey.get(key);
      if (def) return getOutcomeColorClass(def.color);
      return CALL_OUTCOME_BADGES[key] ?? getOutcomeColorClass(null);
    },
    label: (key: string | null | undefined): string => {
      if (!key) return '';
      return byKey.get(key)?.label ?? key.replace(/_/g, ' ');
    },
  };
}
